import { useState, useEffect, useRef, useCallback, useId } from 'react'
import { ArrowLeft, Send, X, Clock, AlertTriangle, Image as ImageIcon, ChevronDown, ChevronUp } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { clsx } from 'clsx'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  chatService,
  type AppointmentMessage,
  type ChatConsultationStatus,
} from '@lib/supabase/chat.service'
import type { AppointmentMedicalInfo } from '@lib/supabase/appointment.service'
import type { RealtimeChannel } from '@supabase/supabase-js'
import Button from './Button'
import { queryKeys } from '@lib/queryKeys'
import { analyticsService } from '@lib/analytics/posthog.service'

// ---- helpers ----------------------------------------------------------------
function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

function formatDateLabel(iso: string) {
  const d = new Date(iso)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  if (d.toDateString() === today.toDateString()) return 'Today'
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function groupByDate(messages: AppointmentMessage[]) {
  const groups: { label: string; messages: AppointmentMessage[] }[] = []
  let currentLabel = ''
  for (const msg of messages) {
    const label = formatDateLabel(msg.created_at)
    if (label !== currentLabel) {
      groups.push({ label, messages: [msg] })
      currentLabel = label
    } else {
      groups[groups.length - 1].messages.push(msg)
    }
  }
  return groups
}

function formatCountdown(seconds: number) {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (h > 0) return `${h}h ${m}m remaining`
  if (m > 0) return `${m}m ${s}s remaining`
  return `${s}s remaining`
}

// ---- Consultation Banner ----------------------------------------------------
function ConsultationBanner({
  status,
  showContinuationCta,
  onRequestContinuation,
}: {
  status: ChatConsultationStatus
  showContinuationCta: boolean
  onRequestContinuation: () => void
}) {
  const [countdown, setCountdown] = useState<number | null>(status.secondsRemaining ?? null)

  useEffect(() => {
    if (!status.isChatConsultation || !status.isActive) return
    if (countdown === null || countdown <= 0) return
    const interval = setInterval(() => setCountdown((c) => (c !== null && c > 0 ? c - 1 : 0)), 1000)
    return () => clearInterval(interval)
  }, [status.isChatConsultation, status.isActive, countdown])

  if (!status.isChatConsultation) return null

  if (status.status === 'expired') {
    return (
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-red-50 border-b border-red-100 text-sm text-red-600">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle size={14} className="shrink-0" />
          <span>This chat consultation has ended.</span>
        </div>
        {showContinuationCta && (
          <button
            onClick={onRequestContinuation}
            className="shrink-0 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 transition-colors"
          >
            Request Continuation
          </button>
        )}
      </div>
    )
  }

  if (status.status === 'completed') {
    return (
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-red-50 border-b border-red-100 text-sm text-red-600">
        <div className="flex items-center gap-2 min-w-0">
          <AlertTriangle size={14} className="shrink-0" />
          <span>This chat consultation has ended.</span>
        </div>
        {showContinuationCta && (
          <button
            onClick={onRequestContinuation}
            className="shrink-0 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 transition-colors"
          >
            Request Continuation
          </button>
        )}
      </div>
    )
  }

  if (status.status === 'not_started') {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 bg-yellow-50 border-b border-yellow-100 text-sm text-yellow-700">
        <Clock size={14} />
        <span>
          Chat starts at {status.startsAt ? new Date(status.startsAt).toLocaleString() : '—'}
        </span>
      </div>
    )
  }

  if (status.isActive && countdown !== null && countdown > 0) {
    const isUrgent = countdown < 300
    return (
      <div className={clsx(
        'flex items-center gap-2 px-4 py-2.5 border-b text-sm',
        isUrgent
          ? 'bg-orange-50 border-orange-100 text-orange-700'
          : 'bg-emerald-50 border-emerald-100 text-emerald-700'
      )}>
        <Clock size={14} />
        <span>{formatCountdown(countdown)}</span>
      </div>
    )
  }

  return null
}

// ---- Message Bubble ---------------------------------------------------------
function MessageBubble({
  msg,
  isOwn,
  onImageClick,
}: {
  msg: AppointmentMessage
  isOwn: boolean
  onImageClick: (url: string) => void
}) {
  const attachment = msg.attachments?.[0]

  return (
    <div className={clsx('flex', isOwn ? 'justify-end' : 'justify-start')}>
      <div
        className={clsx(
          'max-w-[75%] rounded-2xl px-3.5 py-2.5 space-y-1.5',
          isOwn
            ? 'bg-primary text-primary-foreground rounded-br-sm'
            : 'bg-card border border-border text-foreground rounded-bl-sm'
        )}
      >
        {attachment?.signedUrl && (
          <button
            className="block overflow-hidden rounded-xl"
            onClick={() => onImageClick(attachment.signedUrl!)}
          >
            <img
              src={attachment.signedUrl}
              alt="attachment"
              className="max-w-full max-h-64 object-cover rounded-xl"
            />
          </button>
        )}
        {msg.message_text && (
          <p className="text-sm leading-relaxed break-words whitespace-pre-wrap">{msg.message_text}</p>
        )}
        <p className={clsx('text-xs', isOwn ? 'text-primary-foreground/60' : 'text-muted-foreground')}>
          {formatTime(msg.created_at)}
        </p>
      </div>
    </div>
  )
}

// ---- Image Lightbox ---------------------------------------------------------
function Lightbox({ url, onClose }: { url: string; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <button
        title="Close image preview"
        className="absolute top-4 right-4 text-white bg-black/40 rounded-full p-2"
        onClick={onClose}
      >
        <X size={20} />
      </button>
      <img
        src={url}
        alt="fullscreen attachment"
        className="max-w-full max-h-full object-contain rounded-xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  )
}

// ---- Medical Info Pin (provider only) --------------------------------------
function MedicalInfoPin({ info }: { info: AppointmentMedicalInfo }) {
  const [expanded, setExpanded] = useState(true)

  const rows: { label: string; value: string | number | null | undefined }[] = [
    { label: 'Current Symptoms',   value: info.currentSymptoms },
    { label: 'Medical History',    value: info.medicalHistory },
    { label: 'Allergies',          value: info.allergies },
    { label: 'Current Medications',value: info.currentMedications },
    { label: 'Surgery History',    value: info.surgeryHistory },
    { label: 'Family History',     value: info.familyHistory },
    { label: 'Accident Record',    value: info.accidentRecord },
    { label: 'Blood Type',         value: info.bloodType },
    { label: 'Height',             value: info.heightCm != null ? `${info.heightCm} cm` : null },
    { label: 'Weight',             value: info.weightKg != null ? `${info.weightKg} kg` : null },
  ].filter((r) => r.value != null && r.value !== '')

  if (rows.length === 0) return null

  return (
    <div className="shrink-0 bg-blue-50 border-b border-blue-200">
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left"
      >
        <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
          Patient Medical Summary
        </span>
        {expanded ? (
          <ChevronUp size={15} className="text-blue-600 shrink-0" />
        ) : (
          <ChevronDown size={15} className="text-blue-600 shrink-0" />
        )}
      </button>
      {expanded && (
        <div className="px-4 pb-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {rows.map(({ label, value }) => (
            <div key={label} className="flex gap-1.5 min-w-0">
              <span className="text-xs font-medium text-blue-600 shrink-0">{label}:</span>
              <span className="text-xs text-blue-900 break-words min-w-0">{String(value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ---- Main Component ---------------------------------------------------------
export interface AppointmentChatProps {
  appointmentId: string
  patientId: string
  providerId: string
  senderRole: 'patient' | 'provider'
  providerName?: string
  patientName?: string
  backPath: string
  medicalInfo?: AppointmentMedicalInfo | null
}

export default function AppointmentChat({
  appointmentId,
  patientId,
  providerId,
  senderRole,
  providerName,
  patientName,
  backPath,
  medicalInfo,
}: AppointmentChatProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const fileInputId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const composerRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const channelRef = useRef<RealtimeChannel | null>(null)
  const hasTrackedChatOpenedRef = useRef(false)

  const [isSending, setIsSending] = useState(false)
  const [text, setText] = useState('')
  const [pendingImage, setPendingImage] = useState<File | null>(null)
  const [pendingImagePreview, setPendingImagePreview] = useState<string | null>(null)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)
  const [sendError, setSendError] = useState<string | null>(null)
  const [olderMessages, setOlderMessages] = useState<AppointmentMessage[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [oldestCreatedAt, setOldestCreatedAt] = useState<string | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    bottomRef.current?.scrollIntoView({ behavior, block: 'end' })
  }, [])

  const threadQuery = useQuery({
    queryKey: queryKeys.chat.thread(appointmentId),
    queryFn: async () => {
      const [{ messages, hasMore: more, oldestCreatedAt: oldest }, consultationStatus, conversationId] = await Promise.all([
        chatService.getMessagesPage(appointmentId, 20),
        chatService.getConsultationStatus(appointmentId),
        chatService.getConversationId(appointmentId),
      ])

      setHasMore(more)
      setOldestCreatedAt(oldest)
      setOlderMessages([])

      return {
        messages,
        consultationStatus,
        conversationId,
      }
    },
  })

  const handleLoadMore = useCallback(async () => {
    if (!oldestCreatedAt || isLoadingMore) return
    setIsLoadingMore(true)
    try {
      const { messages: older, hasMore: more, oldestCreatedAt: newOldest } =
        await chatService.getMessagesPage(appointmentId, 20, oldestCreatedAt)
      setOlderMessages((prev) => [...older, ...prev])
      setHasMore(more)
      setOldestCreatedAt(newOldest)
    } finally {
      setIsLoadingMore(false)
    }
  }, [appointmentId, oldestCreatedAt, isLoadingMore])

  const messages = [...olderMessages, ...(threadQuery.data?.messages ?? [])]
  const consultationStatus = threadQuery.data?.consultationStatus ?? null
  const conversationId = threadQuery.data?.conversationId ?? null
  const isLoading = threadQuery.isPending

  const addMessage = useCallback((msg: AppointmentMessage) => {
    queryClient.setQueryData<{
      messages: AppointmentMessage[]
      consultationStatus: ChatConsultationStatus
      conversationId: string | null
    } | undefined>(queryKeys.chat.thread(appointmentId), (current) => {
      if (!current) return current
      if (current.messages.some((m) => m.id === msg.id)) return current
      return {
        ...current,
        messages: [...current.messages, msg],
      }
    })
    setTimeout(() => scrollToBottom(), 50)
  }, [appointmentId, queryClient, scrollToBottom])

  useEffect(() => {
    if (!threadQuery.data) return
    setTimeout(() => scrollToBottom('instant'), 80)
  }, [scrollToBottom, threadQuery.data])

  useEffect(() => {
    if (channelRef.current) {
      chatService.unsubscribe(channelRef.current)
      channelRef.current = null
    }

    if (!threadQuery.data) return

    channelRef.current = chatService.subscribeToMessages(
      appointmentId,
      conversationId,
      addMessage
    )

    return () => {
      if (channelRef.current) chatService.unsubscribe(channelRef.current)
    }
  }, [addMessage, appointmentId, conversationId, threadQuery.data])

  useEffect(() => {
    if (hasTrackedChatOpenedRef.current) return
    hasTrackedChatOpenedRef.current = true
    analyticsService.capture('chat_opened', {
      source: 'appointment_chat_component',
      sender_role: senderRole,
      is_chat_consultation: consultationStatus?.isChatConsultation ?? false,
      platform: 'web',
    })
  }, [consultationStatus?.isChatConsultation, senderRole])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const viewport = window.visualViewport
    if (!viewport) {
      rootRef.current?.style.setProperty('--chat-keyboard-offset', '0px')
      return
    }

    const updateKeyboardOffset = () => {
      const keyboardOffset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
      rootRef.current?.style.setProperty('--chat-keyboard-offset', `${keyboardOffset}px`)
    }

    updateKeyboardOffset()
    viewport.addEventListener('resize', updateKeyboardOffset)
    viewport.addEventListener('scroll', updateKeyboardOffset)
    window.addEventListener('orientationchange', updateKeyboardOffset)

    return () => {
      viewport.removeEventListener('resize', updateKeyboardOffset)
      viewport.removeEventListener('scroll', updateKeyboardOffset)
      window.removeEventListener('orientationchange', updateKeyboardOffset)
      rootRef.current?.style.setProperty('--chat-keyboard-offset', '0px')
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined' || !rootRef.current || !composerRef.current) {
      return
    }

    const updateComposerHeight = () => {
      const composerHeight = composerRef.current?.offsetHeight ?? 0
      rootRef.current?.style.setProperty('--chat-composer-height', `${composerHeight}px`)
    }

    updateComposerHeight()

    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => updateComposerHeight())
      : null

    if (resizeObserver && composerRef.current) {
      resizeObserver.observe(composerRef.current)
    }

    window.addEventListener('resize', updateComposerHeight)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updateComposerHeight)
      rootRef.current?.style.setProperty('--chat-composer-height', '0px')
    }
  }, [pendingImagePreview, sendError, text])

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      setSendError('Only JPG, PNG, or WEBP images are allowed.')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setSendError('Image must be 5 MB or smaller.')
      return
    }
    setPendingImage(file)
    setPendingImagePreview(URL.createObjectURL(file))
    setSendError(null)
  }

  const clearImage = () => {
    if (pendingImagePreview) URL.revokeObjectURL(pendingImagePreview)
    setPendingImage(null)
    setPendingImagePreview(null)
  }

  const handleSend = async () => {
    if ((!text.trim() && !pendingImage) || isSending) return
    setSendError(null)
    setIsSending(true)

    analyticsService.capture('chat_message_send_attempt', {
      source: 'appointment_chat_component',
      sender_role: senderRole,
      has_image: Boolean(pendingImage),
      platform: 'web',
    })

    const result = await chatService.sendMessage({
      appointmentId,
      patientId,
      providerId,
      senderRole,
      messageText: text.trim(),
      imageFile: pendingImage,
    })

    setIsSending(false)

    if (result.success) {
      setText('')
      clearImage()
      await threadQuery.refetch()
      setTimeout(() => scrollToBottom(), 80)
    } else {
      analyticsService.capture('chat_message_send_failed', {
        source: 'appointment_chat_component',
        error_type: 'service_returned_failure',
        sender_role: senderRole,
        platform: 'web',
      })
      setSendError(result.error ?? 'Failed to send message.')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const isDisabled =
    consultationStatus?.isChatConsultation &&
    !consultationStatus?.isActive
  const showContinuationCta = Boolean(
    senderRole === 'patient' &&
    consultationStatus?.isChatConsultation &&
    (consultationStatus.status === 'expired' || consultationStatus.status === 'completed')
  )

  const handleRequestContinuation = () => {
    navigate(`/patient/book/${providerId}?service=${encodeURIComponent('Chat Consultation')}&continuationOf=${encodeURIComponent(appointmentId)}`)
  }

  const otherName = senderRole === 'patient' ? (providerName ?? 'Provider') : (patientName ?? 'Patient')
  const groups = groupByDate(messages)

  return (
    <div
      ref={rootRef}
      className="flex h-full min-h-0 flex-col overflow-hidden bg-background [--chat-keyboard-offset:0px] [--chat-composer-height:0px]"
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3.5 bg-card border-b border-border shrink-0">
        <button
          title="Go back"
          onClick={() => navigate(backPath)}
          className="p-1.5 rounded-lg hover:bg-muted transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-semibold text-foreground truncate">{otherName}</h1>
          <p className="text-xs text-muted-foreground">Chat Consultation</p>
        </div>
      </div>

      {/* Consultation Banner */}
      {consultationStatus && (
        <ConsultationBanner
          status={consultationStatus}
          showContinuationCta={showContinuationCta}
          onRequestContinuation={handleRequestContinuation}
        />
      )}

      {/* Medical Info Pin — provider only */}
      {senderRole === 'provider' && medicalInfo && (
        <MedicalInfoPin info={medicalInfo} />
      )}

      {/* Messages */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4 [padding-bottom:calc(var(--chat-composer-height,0px)+var(--chat-keyboard-offset,0px)+1rem)] space-y-5">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-sm text-muted-foreground mt-3">Loading messages…</p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center py-12">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                <Send size={22} className="text-primary" />
              </div>
              <p className="text-sm font-medium text-foreground">No messages yet</p>
              <p className="text-xs text-muted-foreground mt-1">Start the conversation below</p>
            </div>
          </div>
        ) : (
          <>
            {hasMore && (
              <div className="flex justify-center pb-2">
                <button
                  onClick={handleLoadMore}
                  disabled={isLoadingMore}
                  className="text-xs text-primary hover:text-primary/80 font-medium px-4 py-2 rounded-full border border-border bg-card disabled:opacity-50 transition-colors"
                >
                  {isLoadingMore ? 'Loading…' : 'Load older messages'}
                </button>
              </div>
            )}
            {groups.map((group) => (
            <div key={group.label} className="space-y-2">
              {/* Date separator */}
              <div className="flex items-center gap-3 py-1">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground px-2 shrink-0">{group.label}</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              {group.messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  msg={msg}
                  isOwn={msg.sender_role === senderRole}
                  onImageClick={setLightboxUrl}
                />
              ))}
            </div>
          ))}
          </>
        )}
        <div ref={bottomRef} />
      </div>

      <div
        ref={composerRef}
        className="sticky bottom-0 z-10 shrink-0 [transform:translateY(calc(var(--chat-keyboard-offset,0px)*-1))]"
      >
        {/* Send error */}
        {sendError && (
          <div className="mx-4 mb-1 flex items-center gap-2 rounded-xl border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <AlertTriangle size={13} /> {sendError}
          </div>
        )}

        {/* Image preview */}
        {pendingImagePreview && (
          <div className="mx-4 mb-1 flex items-center gap-2 rounded-xl bg-muted px-3 py-2">
            <img src={pendingImagePreview} alt="preview" className="h-10 w-10 rounded-lg object-cover" />
            <span className="flex-1 truncate text-xs text-muted-foreground">
              {pendingImage?.name ?? 'image'}
            </span>
            <button title="Remove selected image" onClick={clearImage} className="text-muted-foreground hover:text-foreground">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Input bar */}
        <div className="border-t border-border bg-card px-4 pt-2 [padding-bottom:calc(max(env(safe-area-inset-bottom),1rem))]">
          <div className="flex items-end gap-2">
          {/* Image upload */}
          <label
            htmlFor={fileInputId}
            className={clsx(
              'flex items-center justify-center w-10 h-10 rounded-xl border border-border bg-background cursor-pointer hover:bg-muted transition-colors shrink-0',
              isDisabled && 'opacity-50 pointer-events-none'
            )}
          >
            <ImageIcon size={18} className="text-muted-foreground" />
            <span className="sr-only">Attach image</span>
          </label>
          <input
            id={fileInputId}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            className="sr-only"
            onChange={handleFileChange}
            disabled={isDisabled}
          />

          {/* Text input */}
          <textarea
            className={clsx(
              'flex-1 resize-none px-3.5 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[42px] max-h-32',
              isDisabled && 'opacity-50 pointer-events-none'
            )}
            placeholder={isDisabled ? 'Type a message…' : 'Type a message…'}
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              // Auto-resize
              e.target.style.height = 'auto'
              e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px'
            }}
            onKeyDown={handleKeyDown}
            rows={1}
            disabled={isDisabled}
          />

          {/* Send button */}
          <Button
            size="sm"
            className="w-10 h-10 p-0 rounded-xl shrink-0"
            onClick={handleSend}
            isLoading={isSending}
            disabled={isDisabled || isSending || (!text.trim() && !pendingImage)}
          >
            {!isSending && <Send size={16} />}
          </Button>
        </div>
      </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && <Lightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />}
    </div>
  )
}
